import React from 'react';
import Card from '@/components/ui/Card';
import { PerformanceMetrics } from '@/types';

interface PerformanceTrendsWidgetProps {
  metrics: PerformanceMetrics;
}

const PerformanceTrendsWidget: React.FC<PerformanceTrendsWidgetProps> = ({ metrics }) => {
  const getTrendIcon = (trend: PerformanceMetrics['ratingTrend']) => {
    switch (trend) {
      case 'improving':
        return (
          <div className="flex items-center text-green-600">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
            <span className="text-sm font-medium">Improving</span>
          </div>
        );
      case 'declining':
        return (
          <div className="flex items-center text-red-600">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
            </svg>
            <span className="text-sm font-medium">Declining</span>
          </div>
        );
      case 'stable':
      default:
        return (
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8" />
            </svg>
            <span className="text-sm font-medium">Stable</span>
          </div>
        );
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 80) return 'text-green-600';
    if (rating >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Card className="h-full">
      <div className="p-4 h-full flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Performance Overview</h3>
        
        {/* Average Rating & Trend */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Team Average Rating</p>
            <p className={`text-3xl font-bold ${getRatingColor(metrics.averageRating)}`}>
              {Math.round(metrics.averageRating * 10) / 10}/100
            </p>
          </div>
          <div className="text-right">
            {getTrendIcon(metrics.ratingTrend)}
          </div>
        </div>

        {/* Performance Categories */}
        <div className="space-y-4">
          {/* Form Players */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">In Form Players</h4>
              <span className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                {metrics.formPlayers.length}
              </span>
            </div>
            {metrics.formPlayers.length > 0 ? (
              <div className="space-y-1">
                {metrics.formPlayers.slice(0, 3).map((player) => (
                  <div key={player.id} className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{player.name}</span>
                    <span className="font-medium text-green-600">{Math.round(player.rating)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No players in exceptional form</p>
            )}
          </div>

          {/* Top Scorers */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Top Scorers</h4>
              <span className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                {metrics.topScorers.length}
              </span>
            </div>
            {metrics.topScorers.length > 0 ? (
              <div className="space-y-1">
                {metrics.topScorers.slice(0, 5).map((player) => (
                  <div key={player.id} className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{player.name}</span>
                    <span className="font-medium text-blue-600">{Math.round(player.rating)} goals</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No goals scored this season</p>
            )}
          </div>

          {/* Top Assists */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300">Top Assists</h4>
              <span className="text-xs bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-full">
                {(metrics.topAssists || []).length}
              </span>
            </div>
            {(metrics.topAssists || []).length > 0 ? (
              <div className="space-y-1">
                {metrics.topAssists.slice(0, 5).map((player) => (
                  <div key={player.id} className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{player.name}</span>
                    <span className="font-medium text-purple-600">{Math.round(player.rating)} assists</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No assists this season</p>
            )}
          </div>

          {/* Out of Form Players */}
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300">Out of Form Players</h4>
              <span className="text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-1 rounded-full font-medium">
                {metrics.underPerformers.length}
              </span>
            </div>
            {metrics.underPerformers.length > 0 ? (
              <div className="space-y-2">
                {metrics.underPerformers.slice(0, 3).map((player) => (
                  <div key={player.id} className="flex justify-between items-center text-sm">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{player.name}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-red-600">{Math.round(player.rating)}</span>
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                      </svg>
                    </div>
                  </div>
                ))}
                {metrics.underPerformers.length > 3 && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
                    +{metrics.underPerformers.length - 3} more players need attention
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-600 dark:text-gray-400">All players performing well</p>
              </div>
            )}
          </div>
        </div>

        {/* Performance Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-green-600">{metrics.formPlayers.length}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">In Form</p>
            </div>
            <div>
              <p className="text-lg font-bold text-blue-600">{metrics.topScorers.length}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Scorers</p>
            </div>
            <div>
              <p className="text-lg font-bold text-purple-600">{(metrics.topAssists || []).length}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Assisters</p>
            </div>
            <div>
              <p className="text-lg font-bold text-red-600">{metrics.underPerformers.length}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Out of Form</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PerformanceTrendsWidget;